/**
 * EventBus - Centralized Event Management System
 * 
 * This module provides a publish-subscribe pattern for decoupled communication
 * between different parts of the application.
 * 
 * @module core/EventBus
 */

class EventBusClass {
  constructor() {
    this.events = new Map();
    this.onceEvents = new Map();
    
    // Track event history in development
    if (import.meta.env.DEV) {
      this.eventHistory = [];
      this.maxHistorySize = 100;
    }
  }
  
  /**
   * Subscribe to an event
   * @param {string} event - Event name
   * @param {Function} callback - Event handler
   * @returns {Function} Unsubscribe function
   */
  on(event, callback) {
    if (!this.events.has(event)) {
      this.events.set(event, new Set());
    }
    
    this.events.get(event).add(callback);
    
    // Return unsubscribe function
    return () => this.off(event, callback);
  }
  
  /**
   * Subscribe to an event only once
   * @param {string} event - Event name
   * @param {Function} callback - Event handler
   * @returns {Function} Unsubscribe function
   */
  once(event, callback) {
    const wrappedCallback = (...args) => {
      callback(...args);
      this.off(event, wrappedCallback);
    };
    
    if (!this.onceEvents.has(event)) {
      this.onceEvents.set(event, new Map());
    }
    
    this.onceEvents.get(event).set(callback, wrappedCallback);
    return this.on(event, wrappedCallback);
  }
  
  /**
   * Unsubscribe from an event
   * @param {string} event - Event name
   * @param {Function} callback - Event handler to remove
   */
  off(event, callback) {
    // Remove from regular events
    if (this.events.has(event)) {
      this.events.get(event).delete(callback);
      
      // Clean up empty sets
      if (this.events.get(event).size === 0) {
        this.events.delete(event);
      }
    }
    
    // Remove from once events
    if (this.onceEvents.has(event)) {
      const onceMap = this.onceEvents.get(event);
      if (onceMap.has(callback)) {
        const wrappedCallback = onceMap.get(callback);
        this.events.get(event)?.delete(wrappedCallback);
        onceMap.delete(callback);
      }
      
      // Clean up empty maps
      if (onceMap.size === 0) {
        this.onceEvents.delete(event);
      }
    }
  }
  
  /**
   * Emit an event
   * @param {string} event - Event name
   * @param {...*} args - Arguments to pass to handlers
   */
  emit(event, ...args) {
    // Log only when explicitly enabled
    const VERBOSE = (typeof window !== 'undefined' && (window.EVENTBUS_DEBUG === true || window.__ENABLE_VERBOSE_LOGS__ === true))
      || (typeof localStorage !== 'undefined' && (localStorage.getItem('enableVerboseLogs') === 'true'));
    if (VERBOSE) {
      this.logEvent(event, args);
    }
    // Optional block for tab changes in diagnostic mode
    const blockTabs = ((typeof window !== 'undefined' && window.__BLOCK_TAB_CHANGES__ === true)
      || (typeof localStorage !== 'undefined' && localStorage.getItem('blockTabChanges') === 'true'))
      && (import.meta.env?.DEV);
    if (blockTabs && event === AppEvents?.TAB_CHANGED) {
      console.warn('⛔ EventBus: TAB_CHANGED blocked');
      return;
    }
    
    // Call all handlers
    if (this.events.has(event)) {
      const handlers = [...this.events.get(event)];
      handlers.forEach(handler => {
        try {
          handler(...args);
        } catch (error) {
          console.error(`Error in event handler for ${event}:`, error);
        }
      });
    }
  }
  
  /**
   * Emit an event asynchronously
   * @param {string} event - Event name
   * @param {...*} args - Arguments to pass to handlers
   * @returns {Promise<void>}
   */
  async emitAsync(event, ...args) {
    const VERBOSE = (typeof window !== 'undefined' && (window.EVENTBUS_DEBUG === true || window.__ENABLE_VERBOSE_LOGS__ === true))
      || (typeof localStorage !== 'undefined' && (localStorage.getItem('enableVerboseLogs') === 'true'));
    if (VERBOSE) {
      this.logEvent(event, args);
    }
    const blockTabs = ((typeof window !== 'undefined' && window.__BLOCK_TAB_CHANGES__ === true)
      || (typeof localStorage !== 'undefined' && localStorage.getItem('blockTabChanges') === 'true'))
      && (import.meta.env?.DEV);
    if (blockTabs && event === AppEvents?.TAB_CHANGED) {
      console.warn('⛔ EventBus: TAB_CHANGED blocked (async)');
      return;
    }
    
    // Call all handlers asynchronously
    if (this.events.has(event)) {
      const handlers = [...this.events.get(event)];
      await Promise.all(
        handlers.map(async handler => {
          try {
            await handler(...args);
          } catch (error) {
            console.error(`Error in async event handler for ${event}:`, error);
          }
        })
      );
    }
  }
  
  /**
   * Clear all handlers for an event
   * @param {string} event - Event name
   */
  clear(event) {
    this.events.delete(event);
    this.onceEvents.delete(event);
  }
  
  /**
   * Clear all event handlers
   */
  clearAll() {
    this.events.clear();
    this.onceEvents.clear();
  }
  
  /**
   * Get all registered events
   * @returns {string[]} Array of event names
   */
  getEvents() {
    return [...new Set([...this.events.keys(), ...this.onceEvents.keys()])];
  }
  
  /**
   * Get handler count for an event
   * @param {string} event - Event name
   * @returns {number} Number of handlers
   */
  getHandlerCount(event) {
    const regularCount = this.events.get(event)?.size || 0;
    const onceCount = this.onceEvents.get(event)?.size || 0;
    return regularCount + onceCount;
  }
  
  /**
   * Log event for debugging
   * @private
   */
  logEvent(event, args) {
    // Runtime kill-switch to reduce console noise during UI debugging
    if (typeof window !== 'undefined' && (window.__DISABLE_VERBOSE_LOGS__ === true || localStorage.getItem('disableVerboseLogs') === 'true')) {
      return;
    }
    const entry = {
      event,
      args: args.length > 0 ? args : undefined,
      timestamp: Date.now(),
      handlers: this.getHandlerCount(event)
    };
    
    this.eventHistory.push(entry);
    
    // Limit history size
    if (this.eventHistory.length > this.maxHistorySize) {
      this.eventHistory.shift();
    }
    
    console.log(`📡 Event: ${event}`, {
      handlers: entry.handlers,
      args: args.length > 0 ? args : '(no args)'
    });
  }
  
  /**
   * Get event history (development only)
   * @returns {Array} Event history
   */
  getHistory() {
    return import.meta.env.DEV ? [...this.eventHistory] : [];
  }
  
  /**
   * Create a namespaced event emitter
   * @param {string} namespace - Namespace prefix
   * @returns {Object} Namespaced event emitter
   */
  namespace(namespace) {
    const prefix = namespace + ':';
    
    return {
      on: (event, callback) => this.on(prefix + event, callback),
      once: (event, callback) => this.once(prefix + event, callback),
      off: (event, callback) => this.off(prefix + event, callback),
      emit: (event, ...args) => this.emit(prefix + event, ...args),
      emitAsync: (event, ...args) => this.emitAsync(prefix + event, ...args),
      clear: (event) => this.clear(prefix + event)
    };
  }
}

// Create singleton instance
const EventBus = new EventBusClass();

// Common application events
export const AppEvents = {
  // Application lifecycle
  APP_INIT: 'app:init',
  APP_READY: 'app:ready',
  APP_ERROR: 'app:error',
  
  // State management
  STATE_CHANGED: 'state:changed',
  STATE_RESET: 'state:reset',
  
  // UI events
  TAB_CHANGED: 'ui:tab-changed',
  MODAL_OPEN: 'ui:modal-open',
  MODAL_CLOSE: 'ui:modal-close',
  LOADING_CHANGED: 'ui:loading-changed',
  TOAST_SHOW: 'ui:toast-show',
  
  // Initiative events
  INITIATIVE_ADDED: 'initiative:added',
  INITIATIVE_UPDATED: 'initiative:updated',
  INITIATIVE_DELETED: 'initiative:deleted',
  INITIATIVE_TRANSFERRED: 'initiative:transferred',
  INITIATIVE_TRANSFER: 'initiative:transfer',
  INITIATIVE_TRANSFER_COMPLETE: 'initiative:transfer-complete',
  INITIATIVE_POPULATE: 'initiative:populate',
  
  // Chat events
  CHAT_MESSAGE_SENT: 'chat:message-sent',
  CHAT_MESSAGE_RECEIVED: 'chat:message-received',
  CHAT_CLEARED: 'chat:cleared',
  CHAT_MESSAGE_ADDED: 'chat:message-added',
  
  // AI events
  AI_REQUEST_START: 'ai:request-start',
  AI_REQUEST_SUCCESS: 'ai:request-success',
  AI_REQUEST_ERROR: 'ai:request-error',
  
  // Portfolio events
  PORTFOLIO_ANALYSIS_START: 'portfolio:analysis-start',
  PORTFOLIO_ANALYSIS_COMPLETE: 'portfolio:analysis-complete',
  PORTFOLIO_CONFLICT_DETECTED: 'portfolio:conflict-detected',

  // Router lifecycle
  ROUTER_READY: 'router:ready',
  
  // Bulk import events
  BULK_IMPORT_START: 'bulk-import:start',
  BULK_IMPORT_COMPLETE: 'bulk-import:complete',
  BULK_IMPORT_ERROR: 'bulk-import:error',

  // Persistence and service events
  INITIATIVES_SAVED: 'initiatives:saved',
  INITIATIVES_CLEARED: 'initiatives:cleared',
  API_KEY_UPDATED: 'api:key-updated',
  ERROR_OCCURRED: 'app:error-occurred'
};

// Export both named and default
export { EventBus, EventBusClass };
export default EventBus;

// Attach to window in development for debugging
if (import.meta.env.DEV) {
  window.EventBus = EventBus;
  window.AppEvents = AppEvents;
  console.log('🔧 EventBus available at window.EventBus for debugging');
}
